import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'
import {MiniCartProvider} from '../../../contexts/mini-cart-context'
import {UnavailableProductsInCartProvider} from '../../../contexts/unavailable-products-in-cart-context'
import {CheckoutProgressProvider} from '../../../contexts/checkout-progress-context'
import {CustomToastProvider} from '../../../contexts/custom-toast-context'
import {GlobalElementsProvider} from '../../../contexts/global-elements-context'
import {HeaderProvider, useHeaderContext} from '../../../contexts/header-context'
import {StickyProvider} from '../../../contexts/sticky-context'
import {AddToCartConfirmationProvider} from '../../../contexts/add-to-cart-confirmation-context'

// Localization
import {IntlProvider} from 'react-intl'

// Components
import ScrollToTop from '../../scroll-to-top'
import CustomToast from '../../custom-toast'
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {SignifydScript} from '../../../utils/signifyd-script'
import {ACTIVE_DATA_ENABLED, DEFAULT_LOCALE, HOME_HREF} from '../../../constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

// Partials
import AppHelmet from './AppHelmet'
import AppSeo from './AppSeo'

/* ------------------------------------------------------------------ */
/*  HeaderVariantSetter – side-effect component (renders null)         */
/* ------------------------------------------------------------------ */

const HeaderVariantSetter = () => {
    const location = useLocation()
    const {setHeaderVariant, setHeaderGradient, setHeaderDropShadow, setHeaderHoverZone} =
        useHeaderContext()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id

    useEffect(() => {
        const params = new URLSearchParams(location?.search)
        const isHomepage = location?.pathname === '/' || location?.pathname === HOME_HREF

        // Transparent header only applies to homepage
        // URL params override env vars for QA testing (homepage only):
        // ?headerVariant=1|2|3  ?headerGradient=white-50|white-30|black-50|black-30|none  ?headerDropShadow=true|false
        const variant = isHomepage
            ? params.get('headerVariant') || getSiteEnvVarValue(appConfig, 'HEADER_HOMEPAGE_VARIANT', siteId) || '3'
            : '3'
        const gradient = isHomepage
            ? params.get('headerGradient') || getSiteEnvVarValue(appConfig, 'HEADER_HOMEPAGE_GRADIENT', siteId) || 'none'
            : 'none'
        const dropShadow = isHomepage
            ? (params.get('headerDropShadow') ?? String(getSiteEnvVarValue(appConfig, 'HEADER_HOMEPAGE_DROP_SHADOW', siteId))) ===
              'true'
            : false

        setHeaderVariant(variant)
        setHeaderGradient(gradient)
        setHeaderDropShadow(dropShadow)
        // Reset hover zone so header starts in correct transparent state
        setHeaderHoverZone('none')
    }, [location?.pathname, location?.search])

    return null
}

/* ------------------------------------------------------------------ */
/*  AppProviders                                                       */
/* ------------------------------------------------------------------ */

const AppProviders = ({
    getTokenWhenReady,
    appConfig,
    normalizedLang,
    targetLocale,
    messages,
    currency,
    isCheckout,
    isSignifydReady,
    basket,
    trackPage,
    children
}) => {
    const {site} = useMultiSite()
    const siteId = site?.id
    const RECAPTCHA_SITE_KEY = getSiteEnvVarValue(appConfig, 'RECAPTCHA_SITE_KEY', siteId)
    return (
    <StorefrontPreview getToken={getTokenWhenReady}>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY || ''}>
            <StickyProvider>
                <HeaderProvider>
                    <HeaderVariantSetter />
                    <CustomToastProvider>
                        <AppHelmet normalizedLang={normalizedLang} appConfig={appConfig} />
                        <IntlProvider
                            onError={(err) => {
                                if (!messages) {
                                    return
                                }
                                if (err.code === 'MISSING_TRANSLATION') {
                                    logger.warn('Missing translation', {
                                        namespace: 'App.IntlProvider',
                                        additionalProperties: {
                                            errorMessage: err.message
                                        }
                                    })
                                    return
                                }
                                throw err
                            }}
                            locale={targetLocale}
                            messages={messages}
                            defaultLocale={DEFAULT_LOCALE}
                        >
                            <CurrencyProvider currency={currency}>
                                <GlobalElementsProvider>
                                    <UnavailableProductsInCartProvider>
                                        <MiniCartProvider>
                                            <AddToCartConfirmationProvider>
                                                <CheckoutProgressProvider>
                                                    <AppSeo />
                                                    <ScrollToTop />
                                                    {children}
                                                </CheckoutProgressProvider>
                                            </AddToCartConfirmationProvider>
                                        </MiniCartProvider>
                                    </UnavailableProductsInCartProvider>
                                </GlobalElementsProvider>
                            </CurrencyProvider>
                        </IntlProvider>
                        {ACTIVE_DATA_ENABLED && (
                            <script
                                type="text/javascript"
                                src={getAssetUrl('static/dwanalytics-22.2.js')}
                                id="dwanalytics"
                                async="async"
                                onLoad={trackPage}
                            ></script>
                        )}
                        {ACTIVE_DATA_ENABLED && (
                            <script
                                src={getAssetUrl('static/dwac-21.7.js')}
                                type="text/javascript"
                                id="dwac"
                                async="async"
                            ></script>
                        )}
                        <CustomToast />
                    </CustomToastProvider>
                    {isSignifydReady && isCheckout && (
                        <SignifydScript sessionId={basket?.c_SignifydOrderSessionId} />
                    )}
                </HeaderProvider>
            </StickyProvider>
        </GoogleReCaptchaProvider>
    </StorefrontPreview>
    )
}

AppProviders.propTypes = {
    getTokenWhenReady: PropTypes.func,
    appConfig: PropTypes.object.isRequired,
    normalizedLang: PropTypes.string.isRequired,
    targetLocale: PropTypes.string,
    messages: PropTypes.object,
    currency: PropTypes.string,
    isCheckout: PropTypes.bool,
    isSignifydReady: PropTypes.bool,
    basket: PropTypes.object,
    trackPage: PropTypes.func,
    children: PropTypes.node
}

export default AppProviders
